// MARK://dhk8/contracts/shared/modules
// Catálogo CANÓNICO de módulos — única fuente de verdad, cross-tier (backend +
// frontend). Vive en @dockethub/contracts/shared porque debe importarse desde:
//   · platform-auth  → calcular el claim `modules` del JWT a partir del rol.
//   · sdk-rbac        → sembrar roles built-in + el catálogo de permisos.
//   · apps/pos, apps/admin-shell → filtrar nav/rutas por el set de módulos del
//     usuario (reemplaza los ROLE_DENY hardcodeados).
//
// Un "módulo" es un área funcional gruesa (pos, catalog, kds, fiscal…). Cada
// módulo expone "superficies" (rutas) en POS y/o Admin, y agrupa las acciones
// (permisos finos) de ese dominio. La selección granular de #3 se hace a nivel
// módulo (lo que el usuario VE); las acciones finas (refund, void…) siguen en el
// claim `permissions` (lo que el usuario PUEDE hacer) — ejes ortogonales.
//
// Relación con las otras dos capas (ver descubrimiento RBAC 2026-06-18):
//   (a) rol → módulos+permisos  [este archivo + sdk-rbac]   — quién, dentro del tenant
//   (b) plan/licencia → módulos ON [platform-licensing]      — qué compró el tenant
//   (c) ModuleFlag.moduleName [comp-tenant-config]           — switch runtime por tenant
// `tenantModule` enlaza cada módulo con su nombre en (b)/(c) para componer luego
// (módulo visible al usuario ⊆ módulos ON del tenant). Hoy (b)/(c) son permisivos.
// ── Catálogo ─────────────────────────────────────────────────────────────────
// Las rutas son prefijos: moduleForRoute() hace match por prefijo más largo, así
// /order/42 → pos, /inventory/warehouses → inventory, /settings/printing → devices.
export const MODULES = [
    {
        key: 'pos', label: 'Punto de venta', tenantModule: 'pos',
        description: 'Operación de venta: plano, comandas, barra, menú, para llevar, tickets.',
        apps: ['pos', 'admin'],
        posRoutes: ['/floor', '/order', '/pos/barra', '/menu', '/para-llevar', '/avisos', '/tickets', '/shift/select', '/offline/cola'],
        adminRoutes: ['/floor'],
    },
    {
        key: 'catalog', label: 'Catálogo', tenantModule: 'catalog',
        description: 'Productos, insumos, modificadores, combos, recetas y menús.',
        apps: ['admin'],
        adminRoutes: ['/catalog', '/combos', '/recipes', '/menus', '/ingredients'],
    },
    {
        key: 'kds', label: 'Cocina (KDS)', tenantModule: 'kds',
        description: 'Pantalla de cocina / despacho.',
        apps: ['pos'],
        posRoutes: ['/kds'],
    },
    {
        key: 'cash', label: 'Caja / Corte',
        description: 'Corte de caja, retiros y arqueo.',
        apps: ['pos'],
        posRoutes: ['/cashout', '/cashcut', '/pos/caja'],
    },
    {
        key: 'reservations', label: 'Reservas y fila', tenantModule: 'reservations',
        description: 'Reservaciones y lista de espera.',
        apps: ['pos', 'admin'],
        posRoutes: ['/reservations', '/waitlist'],
        adminRoutes: ['/reservations', '/waitlist'],
    },
    {
        key: 'fiscal', label: 'Facturación (CFDI)', tenantModule: 'fiscal',
        description: 'Facturación, timbrado CFDI, facturas y clientes fiscales.',
        apps: ['pos', 'admin'],
        posRoutes: ['/billing'],
        adminRoutes: ['/fiscal', '/invoices', '/customers/fiscal'],
    },
    {
        key: 'reports', label: 'Reportes', tenantModule: 'reports',
        description: 'Reportes de ventas y operación.',
        apps: ['pos', 'admin'],
        posRoutes: ['/reports'],
        adminRoutes: ['/reports'],
    },
    {
        key: 'inventory', label: 'Inventario', tenantModule: 'inventory',
        description: 'Existencias y almacenes.',
        apps: ['admin'],
        adminRoutes: ['/inventory'],
    },
    {
        key: 'crm', label: 'Clientes (CRM)', tenantModule: 'crm',
        description: 'Clientes y relación comercial.',
        apps: ['admin'],
        adminRoutes: ['/customers/crm'],
    },
    {
        key: 'purchasing', label: 'Compras',
        description: 'Proveedores y órdenes de compra.',
        apps: ['admin'],
        adminRoutes: ['/suppliers', '/purchase-orders'],
    },
    {
        key: 'staff', label: 'Personal y roles',
        description: 'Alta/edición de personal, roles y permisos.',
        apps: ['admin'],
        adminRoutes: ['/users', '/roles'],
    },
    {
        key: 'devices', label: 'Dispositivos e impresión', tenantModule: 'multi-printer',
        description: 'Terminales, impresoras y configuración de impresión.',
        apps: ['pos', 'admin'],
        posRoutes: ['/settings/printing'],
        adminRoutes: ['/devices'],
    },
    {
        key: 'manager', label: 'Gerencia',
        description: 'Herramientas de gerente / supervisión.',
        apps: ['pos'],
        posRoutes: ['/manager'],
    },
    {
        key: 'settings', label: 'Ajustes',
        description: 'Apariencia y preferencias.',
        apps: ['pos'],
        posRoutes: ['/settings/appearance'],
    },
    {
        key: 'promotions', label: 'Promociones', tenantModule: 'promotions',
        description: 'Promociones y descuentos (futuro).',
        apps: ['admin'],
    },
    {
        key: 'loyalty', label: 'Lealtad', tenantModule: 'loyalty',
        description: 'Programa de lealtad (futuro).',
        apps: ['admin'],
    },
    {
        key: 'franchise', label: 'Franquicias', tenantModule: 'franchise',
        description: 'Multi-sucursal / franquicia (enterprise).',
        apps: ['admin'],
    },
];
/** Todas las claves de módulo (derivado). */
export const MODULE_KEYS = MODULES.map((m) => m.key);
const MODULE_KEY_SET = new Set(MODULE_KEYS);
/** Type guard: ¿es una clave de módulo canónica? */
export function isModuleKey(x) {
    return MODULE_KEY_SET.has(x);
}
// ── Superficies que NO se gatean por módulo ─────────────────────────────────
// Siempre visibles para cualquier usuario autenticado (núcleo + red de seguridad).
export const ALWAYS_ALLOWED_ROUTES = {
    pos: ['/diagnostico', '/login'], // diagnóstico de impresión = red de seguridad (todos los roles)
    admin: ['/dashboard'], // home del admin
};
// Superficies de plataforma (cross-tenant, SCMDG) — NO son módulos de tenant;
// se siguen gateando por rol (superadmin/owner), fuera de este sistema.
export const PLATFORM_ROUTES = {
    pos: [],
    admin: ['/organizations', '/superadmin', '/impersonate'],
};
// ── Resolución ruta → módulo ────────────────────────────────────────────────
function routesFor(app, m) {
    return app === 'pos' ? (m.posRoutes ?? []) : (m.adminRoutes ?? []);
}
/**
 * Módulo dueño de una ruta, por match de PREFIJO más largo (robusto para
 * /order/42, /inventory/warehouses, /settings/printing). `undefined` si la ruta
 * no pertenece a ningún módulo (núcleo, plataforma o ruta desconocida).
 */
export function moduleForRoute(app, pathname) {
    let best;
    let bestLen = -1;
    for (const m of MODULES) {
        for (const r of routesFor(app, m)) {
            if ((pathname === r || pathname.startsWith(r + '/')) && r.length > bestLen) {
                best = m.key;
                bestLen = r.length;
            }
        }
    }
    return best;
}
/** ¿La ruta es "siempre permitida" (núcleo / red de seguridad) en esa app? */
export function isAlwaysAllowedRoute(app, pathname) {
    return ALWAYS_ALLOWED_ROUTES[app].some((r) => pathname === r || pathname.startsWith(r + '/'));
}
/** ¿La ruta es de plataforma (cross-tenant, gateada por rol aparte)? */
export function isPlatformRoute(app, pathname) {
    return PLATFORM_ROUTES[app].some((r) => pathname === r || pathname.startsWith(r + '/'));
}
// ── Roles built-in → módulos ────────────────────────────────────────────────
// Defaults sembrados como Role(builtin=true). Reproducen el gating SANO actual del
// POS (ROLE_DENY) y aplican el MISMO criterio en Admin (que hoy muestra casi todo
// a todos — F3 cierra ese hueco). Los roles custom se definen ENCIMA de esto.
// '*' = todos los módulos (no se enumera para que herede módulos nuevos).
export const BUILTIN_ROLE_MODULES = {
    owner: '*',
    manager: '*',
    // Cajero: opera venta + caja + cocina + reservas + factura + reportes + impresión.
    // Sin gerencia, sin ajustes, sin personal/compras/inventario/CRM.
    cashier: ['pos', 'kds', 'cash', 'reservations', 'fiscal', 'reports', 'devices'],
    // Mesero: toma órdenes + ve cocina + reservas + factura al cliente. Sin caja/reportes.
    waiter: ['pos', 'kds', 'reservations', 'fiscal'],
    // Cocina: solo KDS (+ diagnóstico, que es ALWAYS_ALLOWED).
    kitchen: ['kds'],
};
/**
 * Set de módulos de un rol built-in. Devuelve la lista completa si el rol mapea a
 * '*'. Para roles desconocidos/custom el caller debe resolver desde el motor RBAC;
 * este helper es para el fallback de built-ins (F2) y el seed.
 */
export function builtinRoleModules(role) {
    const set = BUILTIN_ROLE_MODULES[role];
    return set === '*' ? MODULE_KEYS : set;
}
// Normaliza el string de rol legacy (User.role) — EN o ES, case-insensitive — al
// rol built-in canónico. owner/admin/superadmin colapsan a 'owner' (todos los
// módulos). Espejo de normalizeRole() del POS, centralizado aquí.
const LEGACY_ROLE_TO_BUILTIN = {
    owner: 'owner', admin: 'owner', superadmin: 'owner',
    manager: 'manager', gerente: 'manager',
    cashier: 'cashier', cajero: 'cashier',
    waiter: 'waiter', mesero: 'waiter',
    kitchen: 'kitchen', cocina: 'kitchen',
};
/**
 * Módulos visibles derivados del rol legacy `User.role`, para el claim `modules`
 * del JWT (F2a) mientras no exista rol custom. Devuelve `null` si el rol no es un
 * built-in reconocido → el cliente cae al gate legacy (cero regresión en rollout).
 */
export function modulesForLegacyRole(role) {
    if (role === null || role === undefined)
        return null;
    const builtin = LEGACY_ROLE_TO_BUILTIN[role.trim().toLowerCase()];
    return builtin !== undefined ? builtinRoleModules(builtin) : null;
}
//# sourceMappingURL=modules.js.map